<script setup>
import { ref, onMounted, onUnmounted, watch, computed, nextTick } from 'vue';
import gsap from 'gsap';
import Button from './Button.vue';

// Group tech stack by categories
const categories = [
  {
    name: 'Frontend',
    technologies: [
      {
        name: "Vue.js",
        description: "Met Vue.js bouwen wij interactieve, snelle gebruikersinterfaces die uw klanten waarderen. We ontwikkelen single-page applicaties die zorgen voor betere conversies, meer gebruikerstevredenheid en een professionele uitstraling voor uw merk.",
        logo: "/img/tech/vue.svg",
        color: "text-emerald-600"
      },
      {
        name: "TailwindCSS",
        description: "TailwindCSS stelt ons in staat efficiënt prachtige, responsieve interfaces te bouwen met een consistent design. De utility-first aanpak zorgt voor snellere ontwikkeling en betere prestaties van uw applicatie.",
        logo: "/img/tech/tailwind.svg",
        color: "text-sky-500"
      },
      {
        name: "JavaScript",
        description: "Met onze JavaScript expertise transformeren we statische websites in dynamische platforms. Wij zorgen voor interactieve gebruikerservaringen die conversies verhogen, gebruikerstevredenheid verbeteren en uw merk versterken.",
        logo: "/img/tech/javascript.svg",
        color: "text-yellow-500"
      }
    ]
  },
  {
    name: 'Backend',
    technologies: [
      {
        name: "Laravel",
        description: "Met Laravel bouwen wij robuuste, onderhoudbare platformen die meegroeien met je bedrijf. Onze expertise in dit PHP-framework stelt ons in staat complexe bedrijfsprocessen te vereenvoudigen en te automatiseren, met optimale beveiliging en prestaties.",
        logo: "/img/tech/laravel.svg",
        color: "text-red-600"
      },
      {
        name: "C#",
        description: "C# gebruiken we voor het ontwikkelen van krachtige, enterprise-grade applicaties met .NET. Hiermee bouwen we schaalbare, veilige oplossingen die naadloos integreren met bestaande Microsoft-infrastructuur.",
        logo: "/img/tech/csharp.svg",
        color: "text-purple-600"
      }
    ]
  },
  {
    name: 'Mobile',
    technologies: [
      {
        name: "Flutter",
        description: "Met Flutter ontwikkelen we prachtige, native mobiele apps voor zowel iOS als Android vanuit één codebase. Dit levert aanzienlijke kosten- en tijdbesparingen op zonder in te leveren op kwaliteit of gebruikerservaring.",
        logo: "/img/tech/flutter.svg",
        color: "text-cyan-500"
      }
    ]
  },
  {
    name: 'Design & Tools',
    technologies: [
      {
        name: "Figma",
        description: "Figma is ons primaire design tool waarmee we gebruikersinterfaces ontwerpen en prototypes maken. Door klanten vroeg in het proces hierbij te betrekken, zorgen we voor ontwerpen die volledig aansluiten op de verwachtingen.",
        logo: "/img/tech/figma.svg",
        color: "text-pink-500"
      },
      {
        name: "DevOps",
        description: "Met Microsoft DevOps zorgen we voor geautomatiseerde CI/CD pipelines, effectieve projectplanning en naadloze samenwerking tussen ontwikkel- en operationele teams. Dit resulteert in snellere releases en hogere kwaliteit.",
        logo: "/img/tech/devops.svg",
        color: "text-blue-600"
      },
      {
        name: "GitHub",
        description: "GitHub gebruiken we voor versiebeheer, code review en geautomatiseerde workflows. Dit garandeert hoge codekwaliteit, soepele samenwerking en eenvoudige implementatie van nieuwe features en updates.",
        logo: "/img/tech/github.svg",
        color: "text-gray-600"
      }
    ]
  }
];

// Flatten all technologies into a single array for display
const allTechnologies = categories.flatMap(category => 
  category.technologies.map(tech => ({
    ...tech,
    category: category.name // Add category name to each technology item
  }))
);

// Refs for animations
const techStackRef = ref(null);
const headingRef = ref(null);
const descriptionRef = ref(null);

// State for the currently selected technology and auto-rotation
const selectedTech = ref(allTechnologies[0]);
const autoRotationActive = ref(true);
const isInViewport = ref(false);
let rotationInterval = null;
const rotationDelay = 5000; // 5 seconds between rotations

// State for active category (only used for mobile)
const activeCategory = ref(0);

// Get current category technologies (for mobile/tablet view)
const currentTechnologies = computed(() => {
  return categories[activeCategory.value].technologies;
});

// For tabs navigation
const tabsContainer = ref(null);
const activeIndicator = ref(null);

// Function to animate tech items
const animateTechItems = () => {
  const techItems = techStackRef.value.querySelectorAll('.tech-item');
  
  gsap.fromTo(
    techItems,
    { opacity: 0, y: 20, scale: 0.95 },
    {
      opacity: 1,
      y: 0,
      scale: 1,
      duration: 0.5,
      stagger: 0.1,
      ease: "back.out(1.2)"
    }
  );
};

// Function to start the auto-rotation
const startAutoRotation = () => {
  if (rotationInterval) clearInterval(rotationInterval);
  
  rotationInterval = setInterval(() => {
    if (autoRotationActive.value && isInViewport.value) {
      // Find the current index and move to the next one
      const currentIndex = allTechnologies.findIndex(tech => 
        tech.name === selectedTech.value.name && tech.category === selectedTech.value.category);
      const nextIndex = (currentIndex + 1) % allTechnologies.length;
      selectedTech.value = allTechnologies[nextIndex];
    }
  }, rotationDelay);
};

// Function to handle tech item click
const selectTech = (tech) => {
  selectedTech.value = tech;
  autoRotationActive.value = false; // Stop auto-rotation when user clicks
  
  if (rotationInterval) clearInterval(rotationInterval);
};

// Function to handle scroll events to restart auto-rotation and detect viewport
const handleScroll = () => {
  if (!techStackRef.value) return;
  
  const rect = techStackRef.value.getBoundingClientRect();
  const windowHeight = window.innerHeight;
  
  // Check if component is in viewport (with some buffer)
  const inView = rect.top < windowHeight && rect.bottom > 0;
  isInViewport.value = inView;
  
  // If component comes back into view and user hasn't manually selected, restart auto-rotation
  if (inView && !autoRotationActive.value) {
    // Re-enable auto-rotation after scrolling back into view
    autoRotationActive.value = true;
  }
};

// Update indicator position when tab changes
const updateIndicatorPosition = () => {
  if (!tabsContainer.value || !activeIndicator.value) return;
  
  const activeTab = tabsContainer.value.querySelector(`.tab-button[data-index="${activeCategory.value}"]`);
  if (activeTab) {
    const containerRect = tabsContainer.value.getBoundingClientRect();
    const tabRect = activeTab.getBoundingClientRect();
    
    activeIndicator.value.style.left = `${tabRect.left - containerRect.left}px`;
    activeIndicator.value.style.width = `${tabRect.width}px`;
  }
};

// Change active category
const changeCategory = (index) => {
  activeCategory.value = index;
  updateIndicatorPosition();
};

// Watch for active category changes to update indicator
watch(activeCategory, () => {
  nextTick(() => {
    updateIndicatorPosition();
  });
});

// Add animation when the component mounts
onMounted(() => {
  // Animate heading and description
  gsap.fromTo(
    headingRef.value,
    { opacity: 0, y: 30 },
    { 
      opacity: 1, 
      y: 0,
      duration: 0.8,
      ease: "power2.out",
      scrollTrigger: {
        trigger: techStackRef.value,
        start: "top 80%",
        toggleActions: "play none none none"
      }
    }
  );
  
  gsap.fromTo(
    descriptionRef.value,
    { opacity: 0, y: 30 },
    { 
      opacity: 1, 
      y: 0,
      duration: 0.8,
      delay: 0.2,
      ease: "power2.out",
      scrollTrigger: {
        trigger: techStackRef.value,
        start: "top 80%",
        toggleActions: "play none none none"
      }
    }
  );

  // Initial animation for tech items
  animateTechItems();

  // Start auto-rotation
  startAutoRotation();
  
  // Initialize tabs
  nextTick(() => {
    updateIndicatorPosition();
  });
  window.addEventListener('resize', updateIndicatorPosition);

  // Initial viewport check
  handleScroll();
  
  // Add scroll event listener
  window.addEventListener('scroll', handleScroll);
});

// Add computed property for background image
const techBackgroundImage = computed(() => {
  // Map of technology names to background images (replace with actual image paths)
  const backgroundImages = {
    'Laravel': '/img/tech/backgrounds/laravel-bg.jpg',
    'Vue.js': '/img/tech/backgrounds/vue-bg.jpg',
    'PHP': '/img/tech/backgrounds/php-bg.jpg',
    'JavaScript': '/img/tech/backgrounds/javascript-bg.jpg',
    'TailwindCSS': '/img/tech/backgrounds/tailwind-bg.jpg',
    'Alpine.js': '/img/tech/backgrounds/alpine-bg.jpg',
    'React Native': '/img/tech/backgrounds/react-native-bg.jpg',
    'Flutter': '/img/tech/backgrounds/flutter-bg.jpg',
    'Swift': '/img/tech/backgrounds/swift-bg.jpg',
    'Filament': '/img/tech/backgrounds/filament-bg.jpg',
    'Docker': '/img/tech/backgrounds/docker-bg.jpg',
    'Livewire': '/img/tech/backgrounds/livewire-bg.jpg',
  };
  
  // Return the image for the selected tech or a default image
  return backgroundImages[selectedTech.value.name] || '/img/tech/backgrounds/default-bg.jpg';
});

onUnmounted(() => {
  if (rotationInterval) clearInterval(rotationInterval);
  window.removeEventListener('scroll', handleScroll);
  window.removeEventListener('resize', updateIndicatorPosition);
});

// Animate when selected tech changes
watch(selectedTech, () => {
  // Animate the description change
  gsap.fromTo(
    '.tech-description',
    { opacity: 0, y: 10 },
    { opacity: 1, y: 0, duration: 0.4, ease: "power1.out" }
  );
});
</script>

<template>
  <section ref="techStackRef" class="bg-white dark:bg-gray-900 py-16 md:py-24">
    <div class="wide-container relative">
        <!-- Background decorations with responsive opacity -->
        <div class="absolute inset-0 opacity-5 dark:opacity-10 bg-[linear-gradient(to_right,#0000001a_1px,transparent_1px),linear-gradient(to_bottom,#0000001a_1px,transparent_1px)] dark:bg-[linear-gradient(to_right,#ffffff1a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff1a_1px,transparent_1px)] bg-[size:64px_64px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,#000_70%,transparent_110%)]"></div>
        
        <div class="relative z-10">
        <!-- Section header with responsive text colors -->
        <div class="text-center mb-16">
            <h2 ref="headingRef" class="text-pretty text-4xl font-bold text-gray-900 dark:text-white sm:text-5xl lg:text-6xl tracking-tight">
                <span class="bg-custom-rainbow-text text-transparent bg-clip-text">Onze technologieën</span>
            </h2>
            <p ref="descriptionRef" class="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto mt-6">
                We bouwen met de nieuwste frameworks en tools die passen bij jouw specifieke use case en technische behoeften.
            </p>
        </div>
        
        <!-- Two-column layout with responsive theme colors -->
        <div class="flex flex-col lg:flex-row gap-8 mb-12">
            <!-- Left column: Featured image and selected tech description -->
            <div class="lg:w-2/5">
              <!-- Tech showcase image with responsive background -->
              <div class="bg-gray-100 dark:bg-gray-800 rounded-xl overflow-hidden shadow-lg dark:shadow-black/30 border border-gray-200 dark:border-gray-700 mb-6">
                  <div class="relative w-full aspect-video flex items-center justify-center bg-gray-50 dark:bg-gray-900 overflow-hidden">
                    <!-- Background image -->
                    <transition name="fade" mode="out-in">
                      <img :key="`bg-${selectedTech.name}`" :src="techBackgroundImage" :alt="`${selectedTech.name} background`" class="absolute inset-0 w-full h-full object-cover opacity-30 dark:opacity-50"/>
                    </transition>
                    
                    <!-- Responsive overlay -->
                    <div class="absolute inset-0 bg-gradient-to-b from-white/80 to-gray-100/60 dark:from-gray-900/80 dark:to-gray-900/60 backdrop-filter backdrop-blur-sm"></div>
                    
                    <!-- Show the selected technology logo centered -->
                    <div class="relative z-10 flex items-center justify-center">
                      <div class="w-24 h-24 md:w-32 md:h-32 p-6 rounded-full bg-white/90 dark:bg-gray-800/80 backdrop-blur-md flex items-center justify-center shadow-lg border border-gray-200 dark:border-gray-700">
                        <img :src="selectedTech.logo" :alt="selectedTech.name" class="w-full h-full object-contain" />
                      </div>
                    </div>
                    
                    <!-- Subtle radial glow in tech color -->
                    <div class="absolute inset-0 opacity-20 dark:opacity-30 mix-blend-screen" :class="selectedTech.color.replace('text-', 'bg-').replace('-600', '-200').replace('-500', '-100')" style="filter: blur(60px);"></div>
                  </div>
              </div>
              
              <!-- Dynamic tech description with responsive colors -->
              <div class="mb-6 tech-description">
                  <div class="flex items-center gap-3 mb-3">
                    <h3 class="text-2xl font-bold text-gray-900 dark:text-white">{{ selectedTech.name }}</h3>
                    <span class="text-sm text-gray-600 dark:text-gray-400 px-2 py-1 rounded bg-gray-100 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700">{{ selectedTech.category }}</span>
                  </div>
                  <p class="text-gray-700 dark:text-gray-300">{{ selectedTech.description }}</p>
              </div>
              
              <!-- CTA button -->
              <Button :href="`/technologieen/${selectedTech.name.toLowerCase().replace('.', '-')}`" variant="gradient" icon="arrow-right" iconPosition="right">Jouw oplossing versterken met {{ selectedTech.name }}</Button>
            </div>
            
            <!-- Right column: Tech grid with responsive styling -->
            <div class="w-full lg:w-3/5 lg:max-w-2xl ml-auto">
              <!-- Category Tabs for Mobile Only with responsive colors -->
              <div ref="tabsContainer" class="relative flex lg:hidden mb-6 border-b border-gray-200 dark:border-gray-700">
                <button 
                  v-for="(category, index) in categories" 
                  :key="`category-${index}`" 
                  @click="changeCategory(index)" 
                  :class="[
                    'tab-button relative px-4 py-3 text-base font-medium whitespace-nowrap transition-all', 
                    activeCategory === index ? 'text-orange-500' : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                  ]" 
                  :data-index="index"
                >
                  {{ category.name }}
                </button>
                <div ref="activeIndicator" class="absolute bottom-0 h-0.5 bg-orange-500 transition-all duration-300 ease-in-out"></div>
              </div>
              
              <!-- Mobile/Tablet View with responsive styling -->
              <div class="lg:hidden grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div v-for="(tech, index) in currentTechnologies" :key="`mobile-${index}`" 
                    @click="selectTech(tech)"
                    class="tech-item cursor-pointer bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-lg p-4 shadow-lg dark:shadow-black/20 border border-gray-200 dark:border-gray-700 transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                    :class="tech.name === selectedTech.name ? 'ring-2 ring-offset-2 ring-offset-white dark:ring-offset-gray-900 ring-orange-500 bg-orange-50 dark:bg-gray-700/80' : 'hover:bg-gray-50 dark:hover:bg-gray-700/60'">
                
                  <!-- Tech logo with responsive background -->
                  <div class="flex justify-center mb-3 bg-gray-50 dark:bg-gray-900/50 p-3 rounded-lg">
                    <img :src="tech.logo" :alt="`${tech.name} logo`" class="h-14 w-14 object-contain" />
                  </div>
                  
                  <!-- Tech name -->
                  <h4 :class="`text-center font-semibold mb-1 ${tech.color}`">{{ tech.name }}</h4>
                </div>
              </div>
              
              <!-- Desktop View with responsive styling -->
              <div class="hidden lg:grid grid-cols-3 gap-4">
                <div v-for="(tech, index) in allTechnologies" :key="`desktop-${index}`" 
                    @click="selectTech(tech)"
                    class="tech-item cursor-pointer bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-lg p-4 shadow-lg dark:shadow-black/20 border border-gray-200 dark:border-gray-700 transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                    :class="tech.name === selectedTech.name ? 'ring-2 ring-offset-2 ring-offset-white dark:ring-offset-gray-900 ring-orange-500 bg-orange-50 dark:bg-gray-700/80' : 'hover:bg-gray-50 dark:hover:bg-gray-700/60'">
                
                  <!-- Tech logo with responsive background -->
                  <div class="flex justify-center mb-3 bg-gray-50 dark:bg-gray-900/50 p-3 rounded-lg">
                    <img :src="tech.logo" :alt="`${tech.name} logo`" class="h-14 w-14 object-contain" />
                  </div>
                  
                  <!-- Tech name -->
                  <h4 :class="`text-center font-semibold mb-1 ${tech.color}`">{{ tech.name }}</h4>
                  
                  <!-- Category label with responsive text -->
                  <div class="text-center">
                    <span class="text-xs text-gray-500 dark:text-gray-400">{{ tech.category }}</span>
                  </div>
                </div>
              </div>
            </div>
        </div>
        </div>
    </div>
  </section>
</template>

<style scoped>
/* Transitions for tech items */
.tech-item {
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

/* Rainbow text gradient */
.bg-custom-rainbow-text {
  background-image: linear-gradient(90deg, #D95F30, #E68148, #F1A36B);
  background-clip: text;
  -webkit-background-clip: text;
}

/* Add pulse animation to active indicator */
.bg-orange-500 {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}
</style>
